import { useEffect, useState } from 'react';
import { AtSign, MessageCircle, RefreshCw, Send, Users, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Textarea';
import { invokeToolAppCommunication } from '@/lib/tool-app-communication';
import { cn } from '@/lib/utils';
import type {
  ToolAppCommunicationResponse,
  ToolAppMentionablePrincipal,
} from '@shared/types/tool-app-communications';

interface ToolAppCommunicationPanelProps {
  toolAppKey: string;
}

const principalKey = (principal: ToolAppMentionablePrincipal): string => (
  `${principal.principalKind}:${principal.principalId}`
);

export const ToolAppCommunicationPanel = ({ toolAppKey }: ToolAppCommunicationPanelProps) => {
  const [communication, setCommunication] = useState<ToolAppCommunicationResponse | null>(null);
  const [body, setBody] = useState('');
  const [selectedPrincipalKeys, setSelectedPrincipalKeys] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMessages = async () => {
    setError(null);
    try {
      const response = await invokeToolAppCommunication({ action: 'list', toolAppKey });
      setCommunication(response);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Conversation indisponible.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadMessages();
  }, [toolAppKey]);

  const togglePrincipal = (principal: ToolAppMentionablePrincipal) => {
    const key = principalKey(principal);
    setSelectedPrincipalKeys((current) => (
      current.includes(key)
        ? current.filter((candidate) => candidate !== key)
        : [...current, key]
    ));
  };

  const selectedPrincipals = (communication?.mentionablePrincipals ?? []).filter((principal) => (
    selectedPrincipalKeys.includes(principalKey(principal))
  ));

  const sendMessage = async () => {
    const trimmedBody = body.trim();
    if (!trimmedBody || sending) return;

    setSending(true);
    setError(null);
    try {
      const response = await invokeToolAppCommunication({
        action: 'post',
        toolAppKey,
        body: trimmedBody,
        mentions: selectedPrincipals.map((principal) => ({
          principalKind: principal.principalKind,
          principalId: principal.principalId,
        })),
      });
      setCommunication(response);
      setBody('');
      setSelectedPrincipalKeys([]);
      setPickerOpen(false);
    } catch (postError) {
      setError(postError instanceof Error ? postError.message : 'Message non envoyé.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="mt-6 rounded-2xl border-gray-200 bg-white p-0 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
            <MessageCircle className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-950">Messages</h3>
            <p className="text-xs text-gray-500">Commentez et attirez l’attention d’une personne ou d’une équipe autorisée.</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void loadMessages()} disabled={loading}>
          <RefreshCw className={cn('mr-2 h-3.5 w-3.5', loading && 'animate-spin')} />
          Actualiser
        </Button>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-h-[220px] border-b border-gray-100 p-5 lg:border-b-0 lg:border-r">
          {loading ? (
            <p className="text-sm text-gray-400">Chargement de la discussion…</p>
          ) : (communication?.messages.length ?? 0) === 0 ? (
            <div className="flex min-h-[180px] flex-col items-center justify-center text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-50 text-gray-400">
                <MessageCircle className="h-5 w-5" />
              </div>
              <p className="mt-3 text-sm font-medium text-gray-700">Aucun message pour le moment</p>
              <p className="mt-1 max-w-sm text-xs leading-5 text-gray-400">
                Le fil reste attaché à cet outil, y compris après une nouvelle version.
              </p>
            </div>
          ) : (
            <div className="max-h-[360px] space-y-4 overflow-y-auto pr-2">
              {communication?.messages.map((message) => (
                <article key={message.id} className="rounded-2xl bg-gray-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-gray-800">{message.author.displayName}</p>
                    <time className="text-[11px] text-gray-400" dateTime={message.createdAt}>
                      {new Date(message.createdAt).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </time>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700">{message.body}</p>
                  {message.mentions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {message.mentions.map((mention) => (
                        <Badge
                          key={`${mention.principalKind}:${mention.principalId}`}
                          variant="outline"
                          className="bg-white text-violet-700"
                        >
                          @{mention.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="p-5">
          <label className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">
            Nouveau message
            <Textarea
              value={body}
              maxLength={4000}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Partagez un contexte, une question ou une décision…"
              className="mt-2 min-h-[110px] normal-case tracking-normal"
            />
          </label>

          {selectedPrincipals.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedPrincipals.map((principal) => (
                <button
                  key={principalKey(principal)}
                  type="button"
                  onClick={() => togglePrincipal(principal)}
                  className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700"
                >
                  @{principal.label}
                  <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}

          <div className="relative mt-4 flex items-center justify-between gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPickerOpen((current) => !current)}
              disabled={(communication?.mentionablePrincipals.length ?? 0) === 0}
            >
              <AtSign className="mr-2 h-3.5 w-3.5" />
              Mentionner
            </Button>
            <Button size="sm" loading={sending} disabled={!body.trim()} onClick={() => void sendMessage()}>
              <Send className="mr-2 h-3.5 w-3.5" />
              Envoyer
            </Button>

            {pickerOpen && (
              <div className="absolute bottom-11 left-0 z-20 w-72 rounded-2xl border border-gray-200 bg-white p-2 shadow-xl">
                <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                  Accès déjà autorisé
                </p>
                <div className="mt-1 max-h-56 space-y-1 overflow-y-auto">
                  {communication?.mentionablePrincipals.map((principal) => {
                    const selected = selectedPrincipalKeys.includes(principalKey(principal));
                    return (
                      <button
                        key={principalKey(principal)}
                        type="button"
                        onClick={() => togglePrincipal(principal)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-gray-50',
                          selected && 'bg-violet-50',
                        )}
                      >
                        <Users className="h-4 w-4 shrink-0 text-violet-500" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-gray-800">{principal.label}</span>
                          <span className="block text-xs text-gray-400">{principal.detail}</span>
                        </span>
                        {selected && <span className="text-xs font-semibold text-violet-600">Ajouté</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {error && <p className="mt-3 text-xs leading-5 text-red-600">{error}</p>}
          <p className="mt-4 text-[11px] leading-5 text-gray-400">
            Mentionner informe le contexte du message, mais ne donne jamais accès à l’outil.
          </p>
        </div>
      </div>
    </Card>
  );
};
